import {
  applySafeStorageChanges,
  getSafeStorage,
  readSafeStorage,
  removeSafeStorage,
  writeSafeStorage,
} from "./safeStorage";

type PersistedStorageKind = "local" | "session";
export type PersistedStorageScope = "global" | "user" | "company" | "session";

type PersistedStorageContextByScope = {
  global: undefined;
  user: { userId: string };
  company: { companyId: string };
  session: undefined;
};

type PersistedStorageContext = Exclude<
  PersistedStorageContextByScope[PersistedStorageScope],
  undefined
>;

type PersistedStorageContextArgs<Scope extends PersistedStorageScope> =
  PersistedStorageContextByScope[Scope] extends undefined
    ? []
    : [context: PersistedStorageContextByScope[Scope]];

export type PersistedKeyDefinition<
  Value,
  Scope extends PersistedStorageScope,
> = {
  /** Stable product-domain owner, used in the physical key namespace. */
  owner: string;
  /** Stable value name within the owner namespace. */
  name: string;
  scope: Scope;
  storage: PersistedStorageKind;
  version: number;
  parse: (rawValue: string) => Value | null;
  serialize: (value: Value) => string;
  /** Keys from before this definition adopted the shared convention. */
  legacyKeys?: (...contextArgs: PersistedStorageContextArgs<Scope>) => string[];
};

/**
 * Declare one persisted value's owner, scope, schema version, and codec.
 *
 * Definitions stay beside their owning feature. `definePersistedKey` is the
 * searchable registry boundary; raw Web Storage access is held to a shrinking
 * legacy baseline by the persisted-storage fitness test.
 */
const PERSISTED_KEY_PART = /^[a-z][a-z0-9-]*$/u;

export const definePersistedKey = <Value, Scope extends PersistedStorageScope>(
  definition: PersistedKeyDefinition<Value, Scope>,
): PersistedKeyDefinition<Value, Scope> => {
  if (
    !PERSISTED_KEY_PART.test(definition.owner) ||
    !PERSISTED_KEY_PART.test(definition.name)
  ) {
    throw new Error(
      "Persisted storage owner and name must use lowercase kebab-case.",
    );
  }
  if (!Number.isInteger(definition.version) || definition.version < 1) {
    throw new Error("Persisted storage versions must be positive integers.");
  }
  Object.freeze(definition);
  return definition;
};

const getScopeIdentity = (
  scope: PersistedStorageScope,
  context: PersistedStorageContext | undefined,
): string | null => {
  if (scope === "company") {
    if (!context || !("companyId" in context) || !context.companyId) {
      throw new Error("Company-scoped persistence requires a companyId.");
    }
    return context.companyId;
  }

  if (scope === "user") {
    if (!context || !("userId" in context) || !context.userId) {
      throw new Error("User-scoped persistence requires a userId.");
    }
    return context.userId;
  }

  return null;
};

const buildPersistedStorageKey = (
  definition: Pick<
    PersistedKeyDefinition<never, PersistedStorageScope>,
    "name" | "owner" | "scope" | "version"
  >,
  scopeIdentity: string | null,
): string => {
  const baseKey = `sameday:${definition.owner}:${definition.name}:v${definition.version}`;
  if (scopeIdentity === null) return baseKey;
  return `${baseKey}:${definition.scope}:${encodeURIComponent(scopeIdentity)}`;
};

export const getPersistedStorageKey = <
  Value,
  Scope extends PersistedStorageScope,
>(
  definition: PersistedKeyDefinition<Value, Scope>,
  ...contextArgs: PersistedStorageContextArgs<Scope>
): string => {
  const context = contextArgs[0] as PersistedStorageContext | undefined;
  return buildPersistedStorageKey(
    definition,
    getScopeIdentity(definition.scope, context),
  );
};

const parsePersistedValue = <Value>(
  definition: Pick<
    PersistedKeyDefinition<Value, PersistedStorageScope>,
    "parse"
  >,
  rawValue: string,
): Value | null => {
  try {
    return definition.parse(rawValue);
  } catch {
    return null;
  }
};

export const readPersistedValue = <Value, Scope extends PersistedStorageScope>(
  definition: PersistedKeyDefinition<Value, Scope>,
  ...contextArgs: PersistedStorageContextArgs<Scope>
): Value | null => {
  const context = contextArgs[0] as PersistedStorageContext | undefined;
  const scopeIdentity = getScopeIdentity(definition.scope, context);
  const key = buildPersistedStorageKey(definition, scopeIdentity);
  const rawValue = readSafeStorage(key, definition.storage);
  if (rawValue !== null) {
    const value = parsePersistedValue(definition, rawValue);
    if (value !== null) return value;
  }

  const legacyKeys = definition.legacyKeys?.(...contextArgs) ?? [];
  for (const legacyKey of legacyKeys) {
    const legacyRawValue = readSafeStorage(legacyKey, definition.storage);
    if (legacyRawValue === null) continue;
    const legacyValue = parsePersistedValue(definition, legacyRawValue);
    if (legacyValue === null) continue;

    applySafeStorageChanges(
      [
        { key, value: definition.serialize(legacyValue) },
        ...(legacyKey === key ? [] : [{ key: legacyKey, value: null }]),
      ],
      definition.storage,
    );
    return legacyValue;
  }

  return null;
};

export type PersistedValueInspection<Value> =
  | { state: "value"; value: Value }
  | { state: "missing" }
  | { state: "invalid" }
  | { state: "unavailable" };

/** Inspect one canonical value without flattening storage failure into absence. */
export const inspectPersistedValue = <
  Value,
  Scope extends PersistedStorageScope,
>(
  definition: PersistedKeyDefinition<Value, Scope>,
  ...contextArgs: PersistedStorageContextArgs<Scope>
): PersistedValueInspection<Value> => {
  const context = contextArgs[0] as PersistedStorageContext | undefined;
  const key = buildPersistedStorageKey(
    definition,
    getScopeIdentity(definition.scope, context),
  );
  const storage = getSafeStorage(definition.storage);
  if (!storage) return { state: "unavailable" };

  try {
    const rawValue = storage.getItem(key);
    if (rawValue === null) return { state: "missing" };
    const value = parsePersistedValue(definition, rawValue);
    return value === null ? { state: "invalid" } : { state: "value", value };
  } catch {
    return { state: "unavailable" };
  }
};

export const writePersistedValue = <Value, Scope extends PersistedStorageScope>(
  definition: PersistedKeyDefinition<Value, Scope>,
  value: Value,
  ...contextArgs: PersistedStorageContextArgs<Scope>
): boolean => {
  const context = contextArgs[0] as PersistedStorageContext | undefined;
  const key = buildPersistedStorageKey(
    definition,
    getScopeIdentity(definition.scope, context),
  );
  return writeSafeStorage(key, definition.serialize(value), definition.storage);
};

export const removePersistedValue = <
  Value,
  Scope extends PersistedStorageScope,
>(
  definition: PersistedKeyDefinition<Value, Scope>,
  ...contextArgs: PersistedStorageContextArgs<Scope>
): boolean => {
  const context = contextArgs[0] as PersistedStorageContext | undefined;
  const key = buildPersistedStorageKey(
    definition,
    getScopeIdentity(definition.scope, context),
  );
  return removeSafeStorage(key, definition.storage);
};
